import React from 'react';
import { SkillData, SkillNode, documentEntryHasLinkedGuide } from '@skill-trees/shared';
import { CollapseButton } from '../ui/CollapseButton';
import { NodeSettingsPanel } from './NodeSettingsPanel';

interface DevControlsProps {
    data: SkillData;
    onCollapse: () => void;
    isCollapsed: boolean;
    onAddNode: () => void;
    onSetViewport: () => void;
    selectedNodeId?: string | null;
    onRenameNode: (nodeId: string, newName: string) => void;
    onDeleteNode: (nodeId: string) => void;
    onDeleteLink: (sourceId: string, targetId: string) => void;
    onAddGuide?: (nodeId: string) => void;
    onDeleteGuide?: (nodeId: string) => Promise<boolean>;
    /** DRAFT/undo/redo cluster; narrow viewports host it here instead of the control bar. */
    editStatus?: React.ReactNode;
}

export const DevControls: React.FC<DevControlsProps> = ({ 
    data, 
    onCollapse, 
    isCollapsed, 
    onAddNode, 
    onSetViewport,
    selectedNodeId, 
    onRenameNode, 
    onDeleteNode,
    onDeleteLink,
    onAddGuide,
    onDeleteGuide,
    editStatus,
}) => {
    const selectedNode: SkillNode | null = selectedNodeId ? (data.nodes.find(n => n.id === selectedNodeId) || null) : null;
    const selectedLeafHasLinkedGuide = selectedNode
        ? (() => {
              const entry = data.documents.find((d) => d.nodeId === selectedNode.id);
              return entry !== undefined && documentEntryHasLinkedGuide(entry);
          })()
        : false;

    return (
        <div className={`dev-controls ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="dev-panel-header">
                {isCollapsed ? (
                    // Name and arrow share one button: a single generous touch target
                    // that reopens the panel from either.
                    <button
                        type="button"
                        className="dev-panel-expand-button"
                        onClick={onCollapse}
                        aria-label="Open dev panel"
                    >
                        {selectedNode && (
                            <span className="dev-panel-selected-name">{selectedNode.name}</span>
                        )}
                        <span aria-hidden="true">→</span>
                    </button>
                ) : (
                    <h3>Dev Controls</h3>
                )}
                {editStatus && <div className="dev-panel-header-status">{editStatus}</div>}
                {!isCollapsed && (
                    <CollapseButton
                        direction="left"
                        onClick={onCollapse}
                        className="dev-collapse-button"
                    />
                )}
            </div>
            
            {!isCollapsed && (
                <div className="dev-panel-content">
                    <div className="dev-controls-actions">
                        <button 
                            onClick={onAddNode}
                            className="btn btn-primary"
                        >
                            Add skill leaf
                        </button>
                        <button 
                            onClick={onSetViewport}
                            className="btn btn-secondary"
                        >
                            Set Viewport
                        </button>
                    </div>

                    {selectedNode && (
                        <div className="dev-controls-settings">
                            <NodeSettingsPanel 
                                node={selectedNode}
                                onRename={onRenameNode}
                                onDelete={onDeleteNode}
                                onDeleteLink={onDeleteLink}
                                allNodes={data.nodes}
                                allLinks={data.links}
                                leafHasLinkedGuide={selectedLeafHasLinkedGuide}
                                onAddGuide={
                                    onAddGuide ? () => onAddGuide(selectedNode.id) : undefined
                                }
                                onDeleteGuide={
                                    onDeleteGuide
                                        ? () => onDeleteGuide(selectedNode.id)
                                        : undefined
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
